<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
/* v8 ignore file */
import { consola } from 'consola';
import { onMounted, ref, computed } from 'vue';

import { useUserProfileStore } from '@entities/user';
import { useInstitutionStore } from '@entities/institution';
import { useModalStore } from '@shared/model';
import { useAuthStore } from '~/features/auth';
import { useOnboardingTrigger } from '~/features/onboarding/@x/auth';

const logger = consola.withTag('MaxNumberConfirmation');

const storeModal = useModalStore();
const storeAuth = useAuthStore();
const storeUserProfile = useUserProfileStore();
const storeInstitution = useInstitutionStore();
const { startOnboarding } = useOnboardingTrigger();

const hasClickedMaxLink = ref(false);
const loader = ref(false);
const errorMessage = ref<string | null>(null);

const userPhone = computed(
  () =>
    (storeModal.modalData.userData as any)?.phone ||
    storeUserProfile.getUser?.default_phone_number ||
    '...',
);

const maxVerifyLink = computed(() => storeModal.modalData.maxVerifyLink as string | undefined);

const openMax = () => {
  const url = maxVerifyLink.value;
  if (!url) {
    logger.warn('maxVerifyLink не найдена');
    return;
  }

  try {
    const popup = window.open(url, '_blank');
    if (!popup || popup.closed || popup.closed === undefined) {
      logger.warn('Popup для MAX заблокирован');
    } else {
      hasClickedMaxLink.value = true;
    }
  } catch (error) {
    logger.error('Ошибка при открытии MAX:', error);
  }
};

const handleMaxClick = () => {
  hasClickedMaxLink.value = true;
  setTimeout(() => {
    openMax();
  }, 500);
};

const checkMaxVerified = () => {
  loader.value = true;
  errorMessage.value = null;
  storeAuth
    .checkMaxVerification({
      phoneNumber: userPhone.value as string,
      institutionId: storeInstitution.institution?.id as string | number,
    })
    .then(async () => {
      // Телефон подтверждён — показываем онбординг новичку; если уже показывали, закрываем.
      if (!(await startOnboarding())) {
        storeModal.close();
      }
    })
    .catch((error: any) => {
      errorMessage.value =
        error?.data?.message || 'Номер телефона не подтверждён через MAX';
      setTimeout(() => {
        errorMessage.value = null;
      }, 6000);
    })
    .finally(() => {
      loader.value = false;
    });
};

onMounted(() => {
  setTimeout(() => {
    if (!hasClickedMaxLink.value) {
      openMax();
    }
  }, 1000);
});
</script>

<template>
  <client-only>
    <div class="modelReg">
      <div class="modelReg__grid">
        <div v-if="loader" class="modelReg__alertLoading"></div>
        <div class="modelReg__title">
          <h3>Подтверждение через MAX</h3>
          <p>
            Подтвердите номер телефона
            <strong>{{ userPhone }}</strong>
            через мессенджер MAX
          </p>

          <div class="mb-4 p-3 bg-blue-50 rounded-lg">
            <div class="flex items-start gap-2">
              <UIcon name="i-heroicons-information-circle" class="w-5 h-5 text-blue-500 mt-0.5" />
              <p class="text-sm text-blue-700">MAX откроется автоматически через несколько секунд</p>
            </div>
          </div>

          <button
            class="flex items-center gap-2 px-4 py-3 rounded-full w-full justify-center border-0 text-white bg-[linear-gradient(90deg,_#44CCFF_0%,_#5533EE_66%,_#9933DD_100%)] hover:bg-[linear-gradient(90deg,_#3db8e6_0%,_#4c2ed6_66%,_#8a2fc4_100%)] transition-colors"
            @click="handleMaxClick"
          >
            <IconTheMax class="w-8 h-8 text-white shrink-0" />
            <span>Открыть MAX</span>
          </button>
        </div>

        <p v-if="errorMessage" class="text-red-500 text-center mb-3">
          {{ errorMessage }}
        </p>

        <UiButtonMain
          v-if="hasClickedMaxLink"
          class="btn mt-4"
          color="red"
          size="xl"
          label="Я уже подтвердил(а)"
          block
          type="submit"
          :loading="loader"
          :disabled="loader"
          @click="checkMaxVerified"
        />
      </div>
    </div>
  </client-only>
</template>

<style scoped lang="scss"></style>
